import { getCurrentUserId } from "../../../app/state/appSessionStorage"
import {
  ChatsActionTypes,
  IChatsUIAction,
  IChatsUIState,
  IConversationUserList,
  IGroupConversationUserList,
  IGroupList,
  IGroupMessage,
  IUserOnlineStatus,
  NormalizedChatUser,
  NormalizedChatUsersData
} from "./ChatType"

export const toggleChatTab = (tabName: string): IChatsUIAction => {
  return {
    type: ChatsActionTypes.TOGGLE_CHAT_TAB,
    payload: tabName
  }
}

export const fetchChatUserList = (
  userList: NormalizedChatUsersData
): IChatsUIAction => {
  return {
    type: ChatsActionTypes.CHAT_USER_LIST,
    payload: userList
  }
}

export const fetchConversationUserList = (
  userList: IConversationUserList
): IChatsUIAction => {
  return {
    type: ChatsActionTypes.CHAT_CONVERSION_USER_LIST,
    payload: userList
  }
}

export const resetLiveMessages = (): IChatsUIAction => {
  return {
    type: ChatsActionTypes.RESET_LIVE_MESSAGE,
    payload: []
  }
}

export const updateUserOnlineStatus = (
  userOnlineState: IUserOnlineStatus
): IChatsUIAction => {
  return {
    type: ChatsActionTypes.CHANGE_USER_ONLINE_STATUS,
    payload: userOnlineState
  }
}

export const setCurrentChatId = (userId: string): IChatsUIAction => {
  return {
    type: ChatsActionTypes.CURRENT_CHAT_ID,
    payload: userId
  }
}

export const addNewUserToChatConversation = (
  userId: string
): IChatsUIAction => {
  return {
    type: ChatsActionTypes.ADD_NEW_USER_TO_CONVERSATION_LIST,
    payload: userId
  }
}

export const setChatHistory = (chats: any): IChatsUIAction => {
  return {
    type: ChatsActionTypes.SET_HISTORY,
    payload: chats
  }
}

export const resetNotification = (currentChatId: string): IChatsUIAction => {
  return { type: ChatsActionTypes.RESET_NOTIFICATION,
    payload: currentChatId }
}

export const setHistoryTotalPage = (totalPages: number): IChatsUIAction => {
  return {
    type: ChatsActionTypes.SET_HISTORY_TOTAL_PAGE,
    payload: totalPages
  }
}

export const resetChatState = (state: IChatsUIState): IChatsUIAction => {
  return {
    type: ChatsActionTypes.RESET_CHAT_STATE,
    payload: state
  }
}

export const updateNewMessage = (message: string): IChatsUIAction => {
  return {
    type: ChatsActionTypes.SEND_NEW_MESSAGE,
    payload: message
  }
}

export const setHistoryPage = (pageNumber: number): IChatsUIAction => {
  return {
    type: ChatsActionTypes.SET_HISTORY_PAGE,
    payload: pageNumber
  }
}

// ____________________________ chat-group __________________________________________

export const fetchGroupConversationUserList = (
  groupList: IGroupConversationUserList
): IChatsUIAction => {
  return {
    type: ChatsActionTypes.CHAT_CONVERSION_GROUPS_LIST,
    payload: groupList
  }
}

export const setCurrentChatGroupId = (groupId: string): IChatsUIAction => {
  return {
    type: ChatsActionTypes.CURRENT_CHAT_GROUP_ID,
    payload: groupId
  }
}

export const updateGroupNewMessage = (
  message: IGroupMessage
): IChatsUIAction => {
  return {
    type: ChatsActionTypes.SEND_NEW_GROUP_MESSAGE,
    payload: message
  }
}

export const updateGroupWithReceivedMessage = (
  message: IGroupMessage
): IChatsUIAction => {
  return {
    type: ChatsActionTypes.RECEIVED_NEW_GROUP_MESSAGE,
    payload: message
  }
}

export const setGroupChatHistoryPage = (pageNumber: number): IChatsUIAction => {
  return {
    type: ChatsActionTypes.SET_GROUP_CHAT_HISTORY_PAGE,
    payload: pageNumber
  }
}

export const setGroupChatHistory = (chats: any): IChatsUIAction => {
  return {
    type: ChatsActionTypes.SET_GROUP_CHAT_HISTORY,
    payload: chats
  }
}

export const resetGroupNotification = (
  currentGroupId: string
): IChatsUIAction => {
  return {
    type: ChatsActionTypes.RESET_GROUP_CHAT_NOTIFICATION,
    payload: currentGroupId
  }
}

export const setGroupHistoryTotalPage = (
  totalPages: number
): IChatsUIAction => {
  return {
    type: ChatsActionTypes.SET_GROUP_CHAT_HISTORY_TOTAL_PAGE,
    payload: totalPages
  }
}

export const moveUserToTop = (
  users: IConversationUserList[],
  userId: string
): IConversationUserList[] => {
  const index = users.findIndex((user) => user.userId === userId)
  const user = users[index]

  // If the user is already at the top, return the original array
  if (index === 0) {
    return users
  }

  // Remove the user from its original position using splice()
  users.splice(index, 1)

  // Create a new array with the selected user at the front
  return [user, ...users]
}

export const resetUnreadCount = (
  chatList: IConversationUserList[],
  userId: string
): IConversationUserList[] => {
  const a = chatList.map((chat) => {
    return chat.userId === userId ? { ...chat,
      unreadCount: null } : chat
  })
  return a
}

export const updateConversationList = (
  chatList: IConversationUserList[],
  newMessage: any,
  isSendingMsg?: boolean
): IConversationUserList[] => {
  const updatedUserList = chatList.slice()
  let updatedUser = null

  const userIdToUpdateConversation = isSendingMsg
    ? newMessage.toId
    : newMessage.fromId

  // Find the user object in the array with userId equal to fromId
  for (let i = 0; i < updatedUserList.length; i++) {
    if (updatedUserList[i].userId === userIdToUpdateConversation) {
      // Update the lastMessage, lastMessageTimestamp, and unreadCount properties of the user object
      updatedUser = {
        ...updatedUserList[i],
        lastMessage: newMessage.text,
        lastMessageTimestamp: newMessage.sentDatetime,
        unreadCount: isSendingMsg
          ? null
          : (updatedUserList[i].unreadCount as number) + 1
      }
      // Remove the user object from its current position in the array
      updatedUserList.splice(i, 1)
      break
    }
  }

  // If the user object was found, add it to the beginning of the array
  if (updatedUser) {
    updatedUserList.unshift(updatedUser)
  }

  // Return the updated userList
  return updatedUserList
}

export const getUserByUserId = (
  state: IChatsUIState,
  userId: string
): NormalizedChatUser | null => {
  if(!state.chatUserList.result.includes(userId)) return null
  return state.chatUserList.entities.chatUsersByUserId[userId]
}

export const getUserFromConversionList = (
  state: IChatsUIState,
  userId: string
): IConversationUserList => {
  return state.conversionList.filter(
    (user: IConversationUserList) => user.userId === userId
  )[0]
}

export const resetGroupLiveMessages = (): IChatsUIAction => {
  return {
    type: ChatsActionTypes.RESET_GROUP_LIVE_MESSAGE,
    payload: []
  }
}

export const divideChatMessagesByDay = (chats: any): any => {
  const days: any = []

  chats.forEach((chat: any) => {
    const chatDate = new Date(chat.sentDatetime).toDateString()
    let displayDate = chatDate

    // Get the current date and yesterday's date as formatted strings
    const currentDateStr = new Date().toDateString()
    const yesterdayDateStr = new Date(
      Date.now() - 24 * 60 * 60 * 1000
    ).toDateString()

    // Check if the message was sent today
    if (chatDate === currentDateStr) {
      displayDate = "Today"
    }

    // Check if the message was sent yesterday
    if (chatDate === yesterdayDateStr) {
      displayDate = "Yesterday"
    }

    // Find the day object in the array with date equal to chatDate
    const day = days.find((d: any) => d.date === displayDate)
    if (day) {
      // If day object already exists, push the chat object to the messages array
      day.messages.push(chat)
    } else {
      // If day object doesn't exist, create a new day object with the chat object as the first message
      days.push({
        date: displayDate,
        messages: [chat]
      })
    }
  })

  return days
}

export const updateConversationListByAddingNewUser = (
  state: IChatsUIState,
  userId: string
): IConversationUserList[] => {
  const { conversionList, chatUserList } = state
  const user = chatUserList.entities.chatUsersByUserId[userId]
  const newConversationUser = {
    userId: userId,
    lastMessage: "",
    lastMessageTimestamp: "",
    unreadCount: 0,
    name: `${user.firstName} ${user.lastName}`,
    role: user.role,
    active: user.active || false,
    visible: user.visible || false
  }

  return [newConversationUser, ...conversionList]
}

export const updateUserOnlineStatusInConversationList = (
  state: IChatsUIState,
  userOnlineState: IUserOnlineStatus
): IConversationUserList[] => {
  if (getCurrentUserId() === userOnlineState.userId)
    return state.conversionList
  const updatedUserList = state.conversionList.map((chat) => {
    return chat.userId === userOnlineState.userId
      ? {
        ...chat,
        active: userOnlineState.active,
        visible: userOnlineState.visible
      }
      : chat
  })
  return updatedUserList
}

export const moveGroupToTop = (chatGroupList: IGroupList, groupId: string): IGroupList => {
  const { [groupId]: groupToMove, ...restOfGroups } = chatGroupList
  return {
    [groupId]: groupToMove,
    ...restOfGroups
  }
}

export const getUpdatedChatGroupList = (chatGroupList: IGroupList, payload: any, isSending?: boolean): IGroupList => {
  const group = chatGroupList[payload.toId]
  const updatedList = {
    ...chatGroupList,
    [payload.toId]: {
      ...group,
      lastMessage: payload.text,
      lastMessageTimestamp: payload.sentDatetime,
      unreadCount: isSending ? 0 : group.unreadCount + 1
    }
  }
  return moveGroupToTop(updatedList, payload.toId)
}

interface ObjectWithKey {
  [key: string]: any;
}

export const createObjectByKey = <T extends ObjectWithKey>(
  array: T[],
  key: string
): { [key: string]: T } => {
  const result: { [key: string]: T } = {}

  array.forEach((item: T) => {
    result[item[key]] = item
  })

  return result
}

export const resetGroupUnreadCount = (chatGroupList: IGroupList,
  groupId: string): IGroupList => {
  const group = chatGroupList[groupId]
  if (!group) {
    // Handle error: group not found
    return chatGroupList
  }
  return {
    ...chatGroupList,
    [groupId]: {
      ...group,
      unreadCount: 0
    }
  }
}
